'use client';

import { ErrorResponse, Log, User, UserManager, UserManagerSettings, WebStorageStateStore } from 'oidc-client-ts';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AuthProvider as OIDCAuthProvider } from 'react-oidc-context';

const isOktaProvider = process.env.AUTH_CLIENT_PROVIDER === 'okta';

// Client id registered on every local Org Leader for browser-driven OIDC.
const ORG_LEADER_CLIENT_ID = 'cribl-spa';

// Primary IdP issuer, used to tell a leader-issued callback apart from the primary one.
export const PRIMARY_AUTH_ISSUER = process.env.AUTH_CLIENT_ISSUER || `https://${process.env.AUTH_CLIENT_DOMAIN}`;

type OAuthProviderContextType = {
  manager: UserManager | null;
  orgLeaderUser: User | null;
  setOrgLeaderUserFromManager: (authBaseUrl: string) => Promise<void>;
  clearOrgLeaderUser: () => Promise<void>;
  getOrgLeaderUserManager: (authBaseUrl: string) => UserManager;
};

const OAuthProviderContext = React.createContext<OAuthProviderContextType>(
  {
    manager: null,
    orgLeaderUser: null,
    setOrgLeaderUserFromManager: async () => {},
    clearOrgLeaderUser: async () => {},
    getOrgLeaderUserManager: () => { throw new Error('OAuthProvider not mounted'); },
  },
);

function createUserManager(): UserManager {
  const settings: UserManagerSettings = {
    authority: PRIMARY_AUTH_ISSUER,
    client_id: process.env.AUTH_CLIENT_ID as string,
    redirect_uri: `${window.location.origin}/callback`,
    post_logout_redirect_uri: `${window.location.origin}/logout`,
    scope: process.env.AUTH_CLIENT_SCOPE,
    // For Gov environments (they use OKTA), we use session storage
    userStore: new WebStorageStateStore({ store: isOktaProvider ? window.sessionStorage : window.localStorage }),
    loadUserInfo: true,
    automaticSilentRenew: true,
    response_type: 'code',
    extraQueryParams: { audience: process.env.AUTH_CLIENT_AUDIENCE_BROWSER as string },
  };

  return new UserManager(settings);
}

function createOrgLeaderUserManager(authBaseUrl: string): UserManager {
  const authority = `${authBaseUrl.replace(/\/$/, '')}/oidc`;

  return new UserManager({
    authority,
    client_id: ORG_LEADER_CLIENT_ID,
    redirect_uri: `${window.location.origin}/callback`,
    scope: 'openid profile email',
    response_type: 'code',
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    // The leader's access tokens carry an aud claim, which its own userinfo endpoint rejects.
    loadUserInfo: false,
  });
}

const OCI_ORG_CONFIG_KEY = 'oci_org_config';

type OciOrgConfig = {
  authBaseUrl: string;
};

export function readOciOrgConfig(): OciOrgConfig | null {
  try {
    const raw = localStorage.getItem(OCI_ORG_CONFIG_KEY);

    return raw ? (JSON.parse(raw) as OciOrgConfig) : null;
  } catch {
    return null;
  }
}

export function persistOciOrgConfig(authBaseUrl: string): void {
  localStorage.setItem(OCI_ORG_CONFIG_KEY, JSON.stringify({ authBaseUrl }));
}

export function isOrgLeaderAuthSupported(): boolean {
  return process.env.AUTH_CLIENT_SUPPORT_OCI_AUTH === 'true';
}

export const OAuthProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const managerRef = useRef<UserManager | null>(null);
  const orgLeaderManagerRef = useRef<UserManager | null>(null);
  const orgLeaderManagerUrlRef = useRef<string | null>(null);
  const [orgLeaderUser, setOrgLeaderUser] = useState<User | null>(null);

  if (typeof window !== 'undefined' && !managerRef.current) {
    managerRef.current = createUserManager();
  }

  const getOrgLeaderUserManager = useCallback((authBaseUrl: string): UserManager => {
    if (!orgLeaderManagerRef.current || orgLeaderManagerUrlRef.current !== authBaseUrl) {
      orgLeaderManagerRef.current = createOrgLeaderUserManager(authBaseUrl);
      orgLeaderManagerUrlRef.current = authBaseUrl;
    }

    return orgLeaderManagerRef.current;
  }, []);

  const clearOrgLeaderUser = useCallback(async () => {
    if (orgLeaderManagerRef.current) {
      await orgLeaderManagerRef.current.removeUser();
    }

    localStorage.removeItem(OCI_ORG_CONFIG_KEY);
    setOrgLeaderUser(null);
  }, []);

  const setOrgLeaderUserFromManager = useCallback(async (authBaseUrl: string) => {
    const user = await getOrgLeaderUserManager(authBaseUrl).getUser();

    // null means no token in storage yet (e.g. we're mid-callback and the code
    // hasn't been exchanged yet). Leave localStorage intact so the callback page
    // can still read oci_org_config and route to the correct manager.
    if (!user) {
      return;
    }

    if (user.expired) {
      await clearOrgLeaderUser();

      return;
    }

    setOrgLeaderUser(user);
    localStorage.setItem(OCI_ORG_CONFIG_KEY, JSON.stringify({ authBaseUrl }));
  }, [getOrgLeaderUserManager, clearOrgLeaderUser]);

  const context: OAuthProviderContextType = React.useMemo(() => ({
    manager: managerRef.current,
    orgLeaderUser,
    setOrgLeaderUserFromManager,
    clearOrgLeaderUser,
    getOrgLeaderUserManager,
  }), [orgLeaderUser, setOrgLeaderUserFromManager, clearOrgLeaderUser, getOrgLeaderUserManager]);

  useEffect(() => {
    if (!isOrgLeaderAuthSupported()) {
      return;
    }

    const config = readOciOrgConfig();

    if (config) {
      void setOrgLeaderUserFromManager(config.authBaseUrl);
    }
  }, [setOrgLeaderUserFromManager]);

  useEffect(() => {
    const manager = managerRef.current;

    if (!manager) {
      return;
    }

    Log.setLogger(console);
    Log.setLevel(Log.WARN);

    const handleSilentRenewError = (error: Error) => {
      const oauthError = error instanceof ErrorResponse ? error.error : error.message;
      const isSessionGone = oauthError === 'login_required' ||
        oauthError === 'consent_required' ||
        oauthError === 'interaction_required';

      if (isSessionGone) {
        manager.stopSilentRenew();
        manager.removeUser();
      }
    };

    manager.events.addSilentRenewError(handleSilentRenewError);

    return () => {
      manager.events.removeSilentRenewError(handleSilentRenewError);
    };
  }, []);

  if (!managerRef.current) {
    return null;
  }

  return (
    <OIDCAuthProvider userManager={managerRef.current} skipSigninCallback>
      <OAuthProviderContext.Provider value={context}>{children}</OAuthProviderContext.Provider>
    </OIDCAuthProvider>
  );
};

export const useOAuthProvider = (): OAuthProviderContextType => React.useContext(OAuthProviderContext);
